import React, { useState } from 'react';
import { Task, TaskSubmission } from '../../types/task';
import TaskVerification from './TaskVerification';
import { VerificationService } from '../../services/verificationService';
import { useLanguage } from '../../context/LanguageContext';

interface TaskDetailsProps {
  selectedTask: Task | null;
  user: any;
  userSubmission: TaskSubmission | null;
  submissions: TaskSubmission[];
  submissionsLoading: boolean;
  screenshotDisplayUrl: string | null;
  submissionScreenshotUrls: Record<string, string | null>;
  verificationSubmitted: boolean;
  onVerificationSubmitted: () => Promise<void>;
  onTaskClose: () => void;
  onBackToTaskList: () => void;
  showMobileView: boolean;
  sidebarCollapsed: boolean;
  onUpdateSubmission?: () => void;
}

const TaskDetails: React.FC<TaskDetailsProps> = ({
  selectedTask,
  user,
  userSubmission,
  submissions,
  submissionsLoading,
  screenshotDisplayUrl,
  submissionScreenshotUrls,
  verificationSubmitted,
  onVerificationSubmitted,
  onTaskClose,
  onBackToTaskList,
  showMobileView,
  sidebarCollapsed,
  onUpdateSubmission
}) => {
  const { t } = useLanguage();
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewingSubmissions, setReviewingSubmissions] = useState<Set<string>>(new Set());
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set());
  const [currentSubmissionIndex, setCurrentSubmissionIndex] = useState(0);
  const [expandedStatusSections, setExpandedStatusSections] = useState<{
    approved: boolean;
    rejected: boolean;
    pending: boolean;
  }>({
    approved: false,
    rejected: false,
    pending: true // Keep pending expanded by default for easier review
  });

  // Handle task review (approve or reject)
  const handleReviewSubmission = async (submissionUserId: string, approved: boolean) => {
    if (!selectedTask || !user) return;

    // Prevent multiple clicks on the same submission
    if (reviewingSubmissions.has(submissionUserId)) {
      return;
    }

    // Add submission to reviewing set
    setReviewingSubmissions(prev => new Set(prev).add(submissionUserId));

    setReviewSuccess(null);
    setReviewError(null);

    try {
      const { success, error } = await VerificationService.reviewSubmission(
        selectedTask.id,
        submissionUserId,
        approved,
        user.id,
        reviewFeedback
      );

      if (error) throw error;

      if (success !== false) {
        setReviewSuccess(approved ? t('taskDetails.submissionApprovedSuccess') : t('taskDetails.submissionRejectedSuccess'));
        setReviewFeedback('');

        // Trigger parent refresh if callback exists
        if (onVerificationSubmitted) {
          await onVerificationSubmitted();
        }
      } else {
        setReviewError(error || (approved ? t('taskDetails.failedToApprove') : t('taskDetails.failedToReject')));
      }

    } catch (err: any) {
      console.error('Review error:', err);
      setReviewError(err.message || (approved ? t('taskDetails.failedToApproveTryAgain') : t('taskDetails.failedToRejectTryAgain')));
    } finally {
      // Remove submission from reviewing set
      setReviewingSubmissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionUserId);
        return newSet;
      });
    }
  };

  // Handle image loading errors
  const handleImageError = (submissionId: string) => {
    setImageLoadErrors(prev => new Set(prev).add(submissionId));
  };

  if (!selectedTask) {
    return (
      <div className="task-details-empty-state">
        <div className="task-details-empty-content">
          <div className="task-details-empty-icon">
            <i className="bi bi-clipboard-check"></i>
          </div>
          <h3 className="task-details-empty-title">{t('taskDetails.selectTask')}</h3>
          <p className="task-details-empty-description">
            {t('taskDetails.selectTaskDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="task-details-container">
      {/* Scrollable content */}
      <div className="task-details-content">
        {/* Publisher Information */}
        {selectedTask.publisher && (
          <section className="task-details-section publisher-section">
            <div className="task-details-publisher-info">
              {/* <div className="publisher-avatar">
                {selectedTask.publisher.avatar_url ? (
                  <img 
                    src={selectedTask.publisher.avatar_url} 
                    alt={selectedTask.publisher.username}
                    className="publisher-avatar-img"
                  />
                ) : (
                  <div className="publisher-avatar-placeholder">
                    <i className="bi bi-person-circle"></i>
                  </div>
                )}
              </div> */}
              <div className="publisher-details">
                <span className="publisher-label">{t('taskDetails.publishedBy')} </span>
                <span className="publisher-name">{selectedTask.publisher.username}</span>
              </div>
            </div>
          </section>
        )}

        {/* Description */}
        <section className="task-details-section">
          <div className="task-details-section-header">
            <h2 className="task-details-section-title">
              <i className="bi bi-file-text"></i>
              {t('taskDetails.taskDescription')}
            </h2>
          </div>
          <div className="task-details-section-content">
            <p className="task-details-description">
              {selectedTask.description || t('taskDetails.noDescription')}
            </p>
          </div>
        </section>

        {/* Instructions */}
        {selectedTask.instructions && (
          <section className="task-details-section">
            <div className="task-details-section-header">
              <h2 className="task-details-section-title">
                <i className="bi bi-lightbulb"></i>
                {t('taskDetails.instructions')}
              </h2>
            </div>
            <div className="task-details-section-content task-details-instructions-card">
              <p className="task-details-instructions">
                {selectedTask.instructions}
              </p>
            </div>
          </section>
        )}

        {/* Requirements/Verification */}
        <section className="task-details-section">
          <div className="task-details-section-header">
            <h2 className="task-details-section-title">
              <i className="bi bi-shield-check"></i>
              {t('taskDetails.verificationRequirements')}
            </h2>
          </div>
          <div className="task-details-section-content">
            <div className="task-details-verification-info">
              {selectedTask.verification_type === 'screenshot' && (
                <div className="verification-requirement">
                  <i className="bi bi-camera"></i>
                  <span>{t('taskDetails.screenshotRequired')}</span>
                </div>
              )}
              {selectedTask.verification_type === 'text_response' && (
                <div className="verification-requirement">
                  <i className="bi bi-chat-text"></i>
                  <span>{t('taskDetails.textResponseRequired')}</span>
                </div>
              )}
              {selectedTask.verification_type === 'email_username' && (
                <div className="verification-requirement">
                  <i className="bi bi-envelope"></i>
                  <span>{t('taskDetails.emailUsernameRequired')}</span>
                </div>
              )}
              {selectedTask.verification_details?.verification_question && (
                <div className="verification-question">
                  <strong>{t('taskDetails.question')}:</strong> {selectedTask.verification_details.verification_question}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Task Creator Notice */}
        {user && selectedTask.created_by === user.id && selectedTask.status === 'open' && (
          <section className="task-details-section">
            <div className="task-details-creator-notice">
              <div className="creator-notice-content">
                <i className="bi bi-info-circle"></i>
                <div>
                  <strong>{t('taskDetails.publisherNotice')}</strong>
                  <p>{t('taskDetails.publisherNoticeText')}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Verification Section */}
        {user &&
          selectedTask.created_by !== user.id &&
          ((!userSubmission || verificationSubmitted === false) && selectedTask.status === 'open') && (
            <section className="task-details-section">
              <div className="task-details-section-header">
                <h2 className="task-details-section-title">
                  <i className="bi bi-upload"></i>
                  {t('taskDetails.submitYourWork')}
                </h2>
              </div>
              <div className="task-details-section-content">
                <div className="task-details-verification-card">
                  <TaskVerification
                    task={selectedTask}
                    userId={user.id}
                    onVerificationSubmitted={onVerificationSubmitted}
                    existingSubmission={userSubmission}
                  />
                </div>
              </div>
            </section>
          )}

        {/* User Submission Status */}
        {user && userSubmission && selectedTask.created_by !== user.id && (
          <section className="task-details-section">
            <div className="task-details-section-header">
              <h2 className="task-details-section-title">
                <i className="bi bi-send"></i>
                {t('taskDetails.yourSubmission')}
              </h2>
            </div>
            <div className="task-details-section-content">
              <div className="task-details-submission-card">
                <div className="submission-meta">
                  <div className={`submission-status-pill status-${userSubmission.status}`}>
                    <i className={`bi ${userSubmission.status === 'approved' ? 'bi-check-circle-fill' :
                      userSubmission.status === 'rejected' ? 'bi-x-circle-fill' :
                        'bi-clock-history'
                      }`}></i>
                    {userSubmission.status === 'approved' ? t('tasks.approved') :
                      userSubmission.status === 'rejected' ? t('tasks.rejected') :
                        userSubmission.status === 'pending' ? t('tasks.pending') :
                          String(userSubmission.status).charAt(0).toUpperCase() + String(userSubmission.status).slice(1)}
                  </div>
                  <span className="submission-date">
                    {new Date(userSubmission.submitted_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                <div className="submission-proof">
                  {/* Screenshot proof */}
                  {userSubmission.verification_type === 'screenshot' && screenshotDisplayUrl && (
                    <div className="proof-screenshot">
                      <img
                        src={screenshotDisplayUrl}
                        alt="Submission Screenshot"
                        className="submission-image"
                      />
                    </div>
                  )}

                  {/* Text response proof */}
                  {userSubmission.verification_type === 'text_response' && userSubmission.proof?.text_response && (
                    <div className="proof-text">
                      <label>{t('taskDetails.yourResponse')}:</label>
                      <div className="response-content">
                        {userSubmission.proof.text_response}
                      </div>
                    </div>
                  )}

                  {/* Email/username proof */}
                  {userSubmission.verification_type === 'email_username' && userSubmission.proof?.email_username && (
                    <div className="proof-email">
                      <label>{t('taskDetails.emailUsername')}:</label>
                      <div className="email-content">
                        {userSubmission.proof.email_username}
                      </div>
                    </div>
                  )}
                </div>

                {/* Feedback for rejected submissions */}
                {userSubmission.status === 'rejected' && userSubmission.feedback && (
                  <div className="submission-feedback">
                    <label>{t('taskDetails.reviewerFeedback')}:</label>
                    <div className="feedback-content">
                      {userSubmission.feedback}
                    </div>
                  </div>
                )}

                {/* Resubmit button for rejected submissions */}
                {userSubmission.status === 'rejected' && onUpdateSubmission && (
                  <div className="submission-actions">
                    <button
                      onClick={onUpdateSubmission}
                      className="task-details-btn primary"
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                      {t('taskDetails.resubmitTask')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Submissions Review Section (for task creators) */}
        {user && selectedTask && selectedTask.created_by === user.id && (
          <section className="task-details-section">
            <div className="task-details-section-header">
              <h2 className="task-details-section-title">
                <i className="bi bi-people"></i>
                {t('taskDetails.submissionsReview')}
              </h2>
            </div>
            <div className="task-details-section-content">
              {reviewSuccess && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle"></i>
                  {reviewSuccess}
                </div>
              )}

              {reviewError && (
                <div className="alert alert-error">
                  <i className="bi bi-exclamation-triangle"></i>
                  {reviewError}
                </div>
              )}

              {submissionsLoading ? (
                <div className="submissions-loading">
                  <div className="loading-ring"></div>
                  <span>{t('taskDetails.loadingSubmissions')}</span>
                </div>
              ) : submissions.length > 0 ? (
                (() => {
                  // Filter submissions by status
                  const pendingSubmissions = submissions.filter(s => s.status === 'pending');
                  const approvedSubmissions = submissions.filter(s => s.status === 'approved');
                  const rejectedSubmissions = submissions.filter(s => s.status === 'rejected');

                  // Show submission summary with clickable status counts
                  const submissionsSummary = (
                    <div className="submissions-summary">
                      <div className="summary-stats">
                        <div
                          className={`stat-item approved ${expandedStatusSections.approved ? 'expanded' : ''} ${approvedSubmissions.length === 0 ? 'disabled' : ''}`}
                          onClick={approvedSubmissions.length > 0 ? () => setExpandedStatusSections(prev => ({
                            ...prev,
                            approved: !prev.approved
                          })) : undefined}
                        >
                          <span className="stat-number">{approvedSubmissions.length}</span>
                          <span className="stat-label">{t('tasks.approved')}</span>
                          {approvedSubmissions.length > 0 && (
                            <i className={`bi ${expandedStatusSections.approved ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                          )}
                        </div>
                        <div
                          className={`stat-item rejected ${expandedStatusSections.rejected ? 'expanded' : ''} ${rejectedSubmissions.length === 0 ? 'disabled' : ''}`}
                          onClick={rejectedSubmissions.length > 0 ? () => setExpandedStatusSections(prev => ({
                            ...prev,
                            rejected: !prev.rejected
                          })) : undefined}
                        >
                          <span className="stat-number">{rejectedSubmissions.length}</span>
                          <span className="stat-label">{t('tasks.rejected')}</span>
                          {rejectedSubmissions.length > 0 && (
                            <i className={`bi ${expandedStatusSections.rejected ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                          )}
                        </div>
                        <div
                          className={`stat-item pending ${expandedStatusSections.pending ? 'expanded' : ''} ${pendingSubmissions.length === 0 ? 'disabled' : ''}`}
                          onClick={pendingSubmissions.length > 0 ? () => setExpandedStatusSections(prev => ({
                            ...prev,
                            pending: !prev.pending
                          })) : undefined}
                        >
                          <span className="stat-number">{pendingSubmissions.length}</span>
                          <span className="stat-label">{t('tasks.pendingReview')}</span>
                          {pendingSubmissions.length > 0 && (
                            <i className={`bi ${expandedStatusSections.pending ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                          )}
                        </div>
                      </div>
                    </div>
                  );

                  return (
                    <div className="submissions-list">
                      {submissionsSummary}

                      {/* Approved submissions section */}
                      {approvedSubmissions.length > 0 && expandedStatusSections.approved && (
                        <div className="status-submissions-section approved-section">
                          <div className="status-submissions-content">
                            {approvedSubmissions.map((submission) => (
                              <div key={submission.id} className="submission-card compact status-approved">
                                <div className="submission-header">
                                  <div className="submission-user">
                                    <span className="user-id">{t('taskDetails.user')} #{submission.user_id.slice(-8)}</span>
                                    <span className="submission-time">
                                      {new Date(submission.submitted_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <div className="submission-status approved">
                                    <i className="bi bi-check-circle-fill"></i>
                                    {t('tasks.approved')}
                                  </div>
                                </div>
                                {submission.feedback && (
                                  <div className="submission-feedback compact">
                                    <span className="feedback-label">{t('taskDetails.feedback')}:</span>
                                    <span className="feedback-text">{submission.feedback}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Rejected submissions section */}
                      {rejectedSubmissions.length > 0 && expandedStatusSections.rejected && (
                        <div className="status-submissions-section rejected-section">
                          <div className="status-submissions-content">
                            {rejectedSubmissions.map((submission) => (
                              <div key={submission.id} className="submission-card compact status-rejected">
                                <div className="submission-header">
                                  <div className="submission-user">
                                    <span className="user-id">{t('taskDetails.user')} #{submission.user_id.slice(-8)}</span>
                                    <span className="submission-time">
                                      {new Date(submission.submitted_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <div className="submission-status rejected">
                                    <i className="bi bi-x-circle-fill"></i>
                                    {t('tasks.rejected')}
                                  </div>
                                </div>
                                {submission.feedback && (
                                  <div className="submission-feedback compact">
                                    <span className="feedback-label">{t('taskDetails.feedback')}:</span>
                                    <span className="feedback-text">{submission.feedback}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Pending submissions section - Clean and simplified */}
                      {pendingSubmissions.length > 0 && expandedStatusSections.pending && (
                        <div className="status-submissions-section pending-section">
                          <div className="status-submissions-content">
                            {pendingSubmissions.map((submission, index) => (
                              <div key={submission.id} className="submission-card pending-review">
                                <div className="submission-header">
                                  <div className="submission-user">
                                    <span className="user-id">{t('taskDetails.user')} #{submission.user_id.slice(-8)}</span>
                                    <span className="submission-time">
                                      {new Date(submission.submitted_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                  <div className="submission-status pending">
                                    <i className="bi bi-clock"></i>
                                    {t('tasks.pendingReview')}
                                  </div>
                                </div>

                                <div className="submission-content">
                                  {/* Screenshot proof */}
                                  {submission.verification_type === 'screenshot' && submission.proof?.screenshot_url && (
                                    <div className="proof-display">
                                      <span className="proof-label">{t('tasks.screenshot')}:</span>
                                      {submissionScreenshotUrls[submission.id] ? (
                                        <img
                                          src={submissionScreenshotUrls[submission.id]!}
                                          alt="Verification screenshot"
                                          className="proof-image"
                                        />
                                      ) : (
                                        <div className="proof-loading">{t('common.loading')}</div>
                                      )}
                                    </div>
                                  )}

                                  {/* Text response proof */}
                                  {submission.verification_type === 'text_response' && submission.proof?.text_response && (
                                    <div className="proof-display">
                                      <span className="proof-label">{t('taskDetails.response')}:</span>
                                      <div className="proof-text">{submission.proof.text_response}</div>
                                    </div>
                                  )}

                                  {/* Email/username proof */}
                                  {submission.verification_type === 'email_username' && submission.proof?.email_username && (
                                    <div className="proof-display">
                                      <span className="proof-label">{t('taskDetails.emailUsername')}:</span>
                                      <div className="proof-text">{submission.proof.email_username}</div>
                                    </div>
                                  )}

                                  {/* Review section */}
                                  <div className="review-section-compact">
                                    <div className="feedback-input-compact">
                                      <textarea
                                        value={reviewFeedback}
                                        onChange={(e) => setReviewFeedback(e.target.value)}
                                        placeholder={t('taskDetails.optionalFeedback')}
                                        rows={2}
                                      />
                                    </div>
                                    <div className="review-actions-compact">
                                      <button
                                        className={`review-btn approve ${reviewingSubmissions.has(submission.user_id) ? 'loading' : ''}`}
                                        onClick={() => handleReviewSubmission(submission.user_id, true)}
                                        disabled={reviewingSubmissions.has(submission.user_id)}
                                      >
                                        <i className="bi bi-check"></i>
                                        {t('taskDetails.approve')}
                                      </button>
                                      <button
                                        className={`review-btn reject ${reviewingSubmissions.has(submission.user_id) ? 'loading' : ''}`}
                                        onClick={() => handleReviewSubmission(submission.user_id, false)}
                                        disabled={reviewingSubmissions.has(submission.user_id)}
                                      >
                                        <i className="bi bi-x"></i>
                                        {t('taskDetails.reject')}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="no-submissions">
                  <i className="bi bi-inbox"></i>
                  <p>{t('taskDetails.noSubmissions')}</p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default TaskDetails;
